import { ReferencedProduct } from '@epicuro-next/data/baskets-dto';
import { BasketItemProductType } from 'graphql-api/baskets-api-interfaces';

import { ExtendedBasketItem } from '../state/reducers/basket-data.reducer';

import { getExistedBasketItem } from './get-existed-basket-items/get-existed-basket-items';

export function processNewItemsSize(
  newItems: ExtendedBasketItem[],
  oldItems: ExtendedBasketItem[],
  addedItems: Map<string, ExtendedBasketItem>,
) {
  newItems
    .filter((item) => item.productType === BasketItemProductType.Size)
    .forEach((newItem) => {
      const key = `${newItem.productId}-${newItem.accountId}-${newItem.addressId}-${newItem.requestedDeliveryDate}`;
      const existingItem = getExistedBasketItem(
        newItem,
        newItem.accountId,
        newItem.addressId as string,
        oldItems,
      );

      if (
        existingItem &&
        existingItem.requestedQuantity === newItem.requestedQuantity
      ) {
        return;
      }

      const itemInAdded = addedItems.get(key);

      if (!itemInAdded || itemInAdded.orderType === newItem.orderType) {
        addedItems.set(key, newItem);
        return;
      }

      addedItems.set(key, {
        ...newItem,
        requestedQuantity:
          newItem.requestedQuantity + (itemInAdded.requestedQuantity ?? 0),
      });
    });
}

export function processNewItemsPrepack(
  newItems: ExtendedBasketItem[],
  oldItems: ExtendedBasketItem[],
  referencedProducts: ReferencedProduct[] | undefined,
  addedItems: Map<string, ExtendedBasketItem>,
) {
  const result = new Map<string, ExtendedBasketItem>();

  newItems
    .filter((item) => item.productType === BasketItemProductType.Prepack)
    .map((item) => {
      const product = referencedProducts?.find((referencedProduct) => {
        return item.productId === referencedProduct.productId;
      });

      return { referencedProduct: product, newItem: item };
    })
    .forEach(
      ({
        referencedProduct,
        newItem,
      }: {
        referencedProduct: ReferencedProduct;
        newItem: ExtendedBasketItem;
      }) => {
        const existingItem = getExistedBasketItem(
          newItem,
          newItem.accountId,
          newItem.addressId as string,
          oldItems,
        );

        if (
          existingItem &&
          existingItem.requestedQuantity === newItem.requestedQuantity
        ) {
          return;
        }

        referencedProduct?.productCompositionPartReferences.forEach((item) => {
          const key = `${item.product.productId}-${newItem.accountId}-${newItem.addressId}-${newItem.requestedDeliveryDate}`;

          const itemReferencedProduct =
            referencedProducts?.find(
              (i) => i.productId === item.product.productId,
            ) ?? {};

          if (!addedItems.has(key)) {
            addedItems.set(key, {
              ...newItem,
              ...itemReferencedProduct,
              prepackTitle: referencedProduct.title,
              requestedQuantity: item.amount * newItem.requestedQuantity,
            });
            return;
          }

          addedItems.set(key, {
            ...newItem,
            ...itemReferencedProduct,
            prepackTitle: referencedProduct.title,
            requestedQuantity:
              item.amount * newItem.requestedQuantity +
              (addedItems.get(key)?.requestedQuantity ?? 0),
          });
        });
      },
    );

  return result;
}

export function processOldItemsSize(
  oldItems: ExtendedBasketItem[],
  newItems: ExtendedBasketItem[],
  removedItems: Map<string, ExtendedBasketItem>,
) {
  oldItems
    .filter((item) => item.productType === BasketItemProductType.Size)
    .forEach((oldItem) => {
      const key = `${oldItem.productId}-${oldItem.accountId}-${oldItem.addressId}-${oldItem.requestedDeliveryDate}`;
      const existingItem = getExistedBasketItem(
        oldItem,
        oldItem.accountId,
        oldItem.addressId as string,
        newItems,
      );

      if (existingItem) {
        return;
      }

      const itemInRemoved = removedItems.get(key);
      if (!itemInRemoved || itemInRemoved.orderType === oldItem.orderType) {
        removedItems.set(key, oldItem);
        return;
      }

      removedItems.set(key, {
        ...oldItem,
        requestedQuantity:
          oldItem.requestedQuantity +
          (removedItems.get(key)?.requestedQuantity ?? 0),
      });
    });
}

export function processOldItemsPrepack(
  oldItems: ExtendedBasketItem[],
  newItems: ExtendedBasketItem[],
  referencedProducts: ReferencedProduct[] | undefined,
  removedItems: Map<string, ExtendedBasketItem>,
) {
  oldItems
    .filter((item) => item.productType === BasketItemProductType.Prepack)
    .map((item) => {
      const product = referencedProducts?.find((referencedProduct) => {
        return item.productId === referencedProduct.productId;
      });

      return { referencedProduct: product, oldItem: item };
    })
    .forEach(({ referencedProduct, oldItem }) => {
      const existingItem = getExistedBasketItem(
        oldItem,
        oldItem.accountId,
        oldItem.addressId as string,
        newItems,
      );

      if (existingItem) {
        return;
      }

      referencedProduct?.productCompositionPartReferences.forEach((item) => {
        const key = `${item.product.productId}-${oldItem.accountId}-${oldItem.addressId}-${oldItem.requestedDeliveryDate}`;

        const itemReferencedProduct =
          referencedProducts?.find(
            (i) => i.productId === item.product.productId,
          ) ?? {};

        if (!removedItems.has(key)) {
          removedItems.set(key, {
            ...oldItem,
            ...itemReferencedProduct,
            prepackTitle: referencedProduct.title,
            requestedQuantity: item.amount * oldItem.requestedQuantity,
          });
          return;
        }

        removedItems.set(key, {
          ...oldItem,
          ...itemReferencedProduct,
          prepackTitle: referencedProduct.title,
          requestedQuantity:
            item.amount * oldItem.requestedQuantity +
            (removedItems.get(key)?.requestedQuantity ?? 0),
        });
      });
    });
}
